const ERRORS_PREFIX = 'weighbridgeOps.errors.';
const REMEDIES_PREFIX = 'weighbridgeOps.remedies.';

/** Instruction shown when an error key has no specific remedy of its own. */
export const DEFAULT_REMEDY_KEY = `${REMEDIES_PREFIX}default`;

/**
 * Maps an error key to its sibling remedy key — `errors.foo` → `remedies.foo`.
 *
 * Callers resolve it with a fallback, so only the errors an operator can actually hit need
 * their own remedy and the rest degrade to the generic instruction:
 *
 *   t(toRemedyKey(errorKey), { defaultValue: t(DEFAULT_REMEDY_KEY) })
 */
export function toRemedyKey(errorKey: string): string {
  if (typeof errorKey !== 'string' || !errorKey.startsWith(ERRORS_PREFIX)) {
    return DEFAULT_REMEDY_KEY;
  }
  return `${REMEDIES_PREFIX}${errorKey.slice(ERRORS_PREFIX.length)}`;
}

/** Raw technical text (SAP/Strapi/OS strings) worth showing to IT but never to the operator. */
export function getTechnicalDetail(
  errorParams: Record<string, unknown> | undefined,
): string | undefined {
  if (!errorParams) return undefined;
  for (const field of ['sapMessage', 'strapiError', 'reason'] as const) {
    const value = errorParams[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

/** Axios codes that mean the request never completed at the transport layer. */
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'] as const;
const NETWORK_CODES = ['ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED', ...TIMEOUT_CODES] as const;

type TransportError = {
  response?: unknown;
  code?: string;
  isAxiosError?: boolean;
};

function asTransportError(err: unknown): TransportError | null {
  if (!err || typeof err !== 'object') return null;
  const candidate = err as TransportError;
  // A response means we reached the server, so the failure is not connectivity.
  if (candidate.response) return null;
  return candidate;
}

/**
 * True when the request never got a reply — no internet, DNS failure, TCP timeout.
 *
 * Deliberately narrow: it must look like an axios transport failure. A plain Error
 * thrown by our own code (a bad transform, a null deref) also has no `response`, and
 * reporting that as "no internet" would send the operator to check a cable over a bug.
 */
export function isNetworkError(err: unknown): boolean {
  const candidate = asTransportError(err);
  if (!candidate) return false;
  if (NETWORK_CODES.includes(candidate.code as (typeof NETWORK_CODES)[number])) return true;
  // Axios threw it and there is no response: still a transport failure, code or not.
  return candidate.isAxiosError === true;
}

/**
 * True when the request was cut off by its own timeout rather than by a dead link.
 * Distinct from `isNetworkError` because the operator's next move differs: a timeout
 * means the connection is up but slow — and on a write, the request may still have
 * landed on the server — whereas no connection means check the cable.
 */
export function isTimeoutError(err: unknown): boolean {
  const candidate = asTransportError(err);
  if (!candidate) return false;
  return TIMEOUT_CODES.includes(candidate.code as (typeof TIMEOUT_CODES)[number]);
}

/**
 * Error key for a Save that never got an answer from the weighbridge controller, or null
 * when the controller did answer (its own messageKey then applies).
 *
 * The controller sits on the plant LAN, so a dead link while the browser is still online
 * means the controller box, not the internet — only an offline browser gets "no internet".
 */
export function resolveSaveTransportErrorKey(err: unknown, browserOnline: boolean): string | null {
  if (isTimeoutError(err)) return `${ERRORS_PREFIX}requestTimeout`;
  if (!isNetworkError(err)) return null;
  return browserOnline
    ? `${ERRORS_PREFIX}controllerRequestFailed`
    : `${ERRORS_PREFIX}strapiUnreachable`;
}
